import AppLayout from '../../components/layouts/AppLayout.js'
import PluginsToolbar from '../../components/plugins/PluginsToolbar'
import Pagination from '../../components/Pagination'
import { client } from '../../services/client'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { GET_APPLICATIONS } from '../../services/graphql/queries/getApplications'
import { GET_LANGUAGES } from '../../services/graphql/queries/getLanguages'
import { GET_CATEGORIES } from '../../services/graphql/queries/getCategories'
import { GET_TAGS } from '../../services/graphql/queries/getTags'
import { useRouter } from 'next/router'
import { useEffect, useReducer, useState } from 'react'
import PluginSearchTitle from '../../components/plugins/PluginSearchTitle'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import Ad from '../../components/Ad'
import PluginFilter from '../../components/plugins/filter/PluginFilter'
import Link from 'next/link'
import Image from 'next/future/image'
import useTranslatedRoutes from '../../services/translatedRoutes'
import useRouteTranslator from '../../hooks/useRouteTranslator'
import AboutCadsoma from '../../components/AboutCadsoma'
import Plugin from '../../components/Plugin'

export default function Plugins ({ applications, categories, tags, languages, hasApplicationQuery, hasCategoryQuery }) {
  const router = useRouter()
  const { getHrefLangLink } = useRouteTranslator(router.locale, router.asPath)
  const [hrefLinks, setHrefLinks] = useState([])
  const { getSoftwareName, getCategoryName } = useTranslatedRoutes(router.locale.toUpperCase())
  const { t } = useTranslation('plugins')
  const [query, updateQuery] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      sort: router.query.sort ?? 'MOST_POPULAR',
      page: router.query.page ?? 1,
      search: router.query.search ?? '',
      filter: router.query.filter
        ? JSON.parse(router.query.filter)
        : { compatibilities: [], categories: [], tags: [], languages: [], rating: 0, price: [] }
    }
  )
  const [plugins, setPlugins] = useState({
    data: [], meta: {}, links: []
  })
  const pluginsWithAds = plugins.data.map(plugin => (
    <div
      className="col-12"
      key={plugin.id}
    >
      <Plugin plugin={plugin} />
    </div>
  ))

  const searchProducts = async () => {
    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL
    const params = new URLSearchParams(router.query)

    const response = await fetch(`${baseUrl}api/products/search?${params.toString()}`)
    return response.json()
  }

  const buildFilter = () => {
    const queryFilter = query.filter
    /**
     * Those are the special cases, where we need to add the application or category to the filter
     * This is because someone clicked the application filter from the landing-page
     */
    if (hasApplicationQuery) {
      const applicationName = getSoftwareName(router.query.application)

      queryFilter.compatibilities = [applicationName]
    }

    /**
     * Those are the special cases, where we need to add the application or category to the filter
     * This is because someone clicked the category filter from the landing-page
     */
    if (hasCategoryQuery) {
      const categoryName = getCategoryName(decodeURI(router.query.category))

      queryFilter.categories = [categoryName]
    }

    /**
     * This probably only occurs, when the user uses the header search bar and is on the plugin page
     */
    if (router.query.search && router.query.search !== query.search) {
      query.search = router.query.search
    }

    return new URLSearchParams({
      ...query,
      filter: JSON.stringify(queryFilter)
    })
  }

  /**
   * Update the query parameters in the URL
   * This is needed so if the user clicks on a single product and changes the url
   * and then clicks back, the filters are still applied
   */
  useEffect(() => {
    if (Object.keys(query.filter).length === 0) return

    const path = router.asPath.split('?')[0]
    const params = buildFilter()

    router.replace(`${path}?${params.toString()}`, undefined, { shallow: true })
  }, [query.sort, query.page, query.filter])

  /**
   * The search is no onInput. It only changes, when the user has selected a keyword
   * When this happens, we reset all filter and only search for the keyword
   */
  useEffect(() => {
    if (!query.search) return

    updateQuery({
      search: query.search,
      filter: { compatibilities: [], categories: [], tags: [], languages: [], rating: 0, price: [] },
      page: 1,
      sort: 'MOST_POPULAR'
    })
  }, [query.search])

  /**
   * Search for new products, when the query changes
   */
  useEffect(() => {
    let isSearching = true

    /**
     * If you are on the product site and select a plugin per software, then it won't use the useEffect to build up
     * the url properly. So we need to do it here.
     */
    if (!router.query.sort) {
      const path = router.asPath.split('?')[0]
      const params = buildFilter()

      router.replace(`${path}?${params.toString()}`, undefined, { shallow: true })

      return
    }

    // Push search event if term is given
    if (query.search) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'search',
        searchTerm: query.search
      })
    }

    // Trigger search
    searchProducts().then(data => {
      if (!hrefLinks.length) {
        const hrefLangLinks = []

        for (const locale of router.locales) {
          const link = getHrefLangLink(locale)
          hrefLangLinks.push([locale, link])
        }

        setHrefLinks(hrefLangLinks)
      }

      if (!isSearching) return

      setPlugins(data)
    })

    return () => {
      isSearching = false
    }
  }, [router.query])

  // Add inline ad
  if (pluginsWithAds.length > 3) {
    pluginsWithAds.splice(
      3,
      0,
      <div
        className="col-12 col-lg-6 text-center"
        key="inlineAd"
      >
        <Ad
          zoneId="5"
          loadScript={false}
        />
      </div>
    )
  }

  return (
    <AppLayout>
      <Head>
        <title>{t('meta.title')}</title>
        {
          hrefLinks.map(link => {
            return (
              <link
                key={link[0]}
                rel="alternate"
                hrefLang={link[0]}
                href={link[1]}
              />
            )
          })
        }
      </Head>
      <div className="appendix appendix--bottomright appendix--turquoise">
        <PluginSearchTitle />
        <div className="container py-5">
          <div
            className="row g-3 gx-md-5 justify-content-between align-items-lg-start"
            style={{ scrollPadding: '100px' }}
          >
            <PluginFilter
              applications={applications}
              categories={categories}
              tags={tags}
              languages={languages}
              plugins={plugins}
              query={query}
              updateQuery={updateQuery}
            />
            <div className="col-12 col-lg-8">
              <div className="row text-center mt-5 d-none d-lg-block">
                <div className="col-12">
                  <Ad
                    zoneId="14"
                    loadScript={false}
                  />
                </div>
              </div>

              <div className="row mb-3 mb-lg-4">
                <PluginsToolbar
                  plugins={plugins}
                  onSortChange={e => updateQuery({ sort: e })}
                  sort={query.sort}
                />
              </div>
              {plugins?.meta?.company_search && (<div className="col-12 mb-5">
                <h2 className="h3 fw-bold text-dark">
                  {t('plugins_by')}:
                  <Link href={`/seller/${plugins.data[0].company.slug}`}>
                    <a className="text-dark-blue ms-2 text-decoration-none">
                      {plugins.data[0].company.name}
                    </a>
                  </Link>
                </h2>
              </div>)}
              <div className="row g-md-4">
                {pluginsWithAds}
              </div>
              {plugins?.meta?.company_search && (<div className="col-12 mt-5">
                <div className="bg-light rounded">
                  <Link href={`/seller/${plugins.data[0].company.slug}`}>
                    <a
                      className="d-flex p-4 align-items-center text-decoration-none text-dark-blue justify-content-around"
                    >
                      {
                        plugins.data[0].company.logo.url && (
                          <Image
                            src={plugins.data[0].company.logo.url}
                            alt=""
                            className="rounded img-fluid"
                            width={150}
                            height={150}
                          />
                        )
                      }
                      <span className="h3 m-0">
                        {plugins.data[0].company.name}
                      </span>
                    </a>
                  </Link>
                </div>
              </div>)}
              {plugins.data.length > 0 && (<div className="row mt-5">
                <Pagination
                  paginatorInfo={{ lastPage: plugins?.meta?.last_page }}
                  page={query.page}
                  onPageChange={e => updateQuery({ page: e })}
                  className={'d-flex justify-content-center'}
                />
              </div>)}
              <div className="row text-center mt-5 d-none d-lg-block">
                <div className="col-12">
                  <Ad
                    zoneId="6"
                    loadScript={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="skybarad">
        <Ad
          zoneId="2"
          loadScript={false}
        />
      </div>
      <AboutCadsoma />
    </AppLayout>
  )
}

export async function getServerSideProps ({ locale, query }) {
  const newClient = client(locale)

  const [{ value: applications }, { value: languages }, { value: categories }, { value: tags }] = await Promise.allSettled([
    newClient.query({
      fetchPolicy: 'no-cache',
      query: GET_APPLICATIONS,
      variables: {
        active_only: true
      }
    }),
    newClient.query({
      query: GET_LANGUAGES
    }),
    newClient.query({
      query: GET_CATEGORIES,
      variables: {
        active_only: true
      }
    }),
    newClient.query({
      query: GET_TAGS,
      variables: {
        active_only: true
      }
    })])

  return {
    props: {
      applications: applications.data,
      languages: languages.data,
      categories: categories.data,
      tags: tags.data,
      /**
       * We do this check here and not on the client site, because...
       * on the client side this query is not available as it is translated
       * from ?application its becoming /plugins/application for instance
       */
      hasApplicationQuery: !!query.application,
      hasCategoryQuery: !!query.category,
      ...(await serverSideTranslations(locale, ['plugins', 'categories', 'tags', 'applications', 'languages', 'footer', 'header', 'common', 'routes']))
    }
  }
}
